import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { ArrowLeft, Building2 } from "lucide-react";
import { Seo } from "@/components/Seo";


const PROFESSIONAL_TYPES = [
  { id: "business", label: "Attività" },
  { id: "local_business", label: "Attività locale" },
  { id: "professional", label: "Professionista" },
  { id: "brand", label: "Brand" },
  { id: "organization", label: "Organizzazione" },
];

const emptyForm = {
  professional_name: "",
  professional_type: "business",
  category: "",
  subcategory: "",
  extended_bio: "",
  website_url: "",
  business_email: "",
  business_phone: "",
  city: "",
  country: "IT",
  address: "",
  accepts_inbound_dm: false,
};

export default function ProfessionalSettings() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, profile_type: "personal", searchable: true });
  const [form, setForm] = useState(emptyForm);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get(`/professional/categories`)
      .then((r) => setCategories(r.data.categories || []))
      .catch(() => setCategories([]));
  }, []);

  const load = async () => {
    try {
      const r = await api.get(`/professional/profile/me`);
      const pp = r.data.professional_profile;
      setState({ loading: false, profile_type: r.data.profile_type || "personal", searchable: pp?.searchable ?? true });
      if (pp) {
        setForm({
          professional_name: pp.professional_name || "",
          professional_type: pp.professional_type || "business",
          category: pp.category || "",
          subcategory: pp.subcategory || "",
          extended_bio: pp.extended_bio || "",
          website_url: pp.website_url || "",
          business_email: pp.business_email || "",
          business_phone: pp.business_phone || "",
          city: pp.city || "",
          country: pp.country || "IT",
          address: pp.address || "",
          accepts_inbound_dm: !!pp.accepts_inbound_dm,
        });
      }
    } catch {
      setState({ loading: false, profile_type: "personal", searchable: true });
    }
  };

  useEffect(() => { load(); }, []);

  const isPro = state.profile_type === "professional";
  const canSubmit = form.professional_name.trim().length >= 2 && form.category.trim().length >= 1 && form.city.trim().length >= 1 && form.country.length === 2;

  const doActivate = async () => {
    if (!canSubmit) { alert("Compila almeno nome, categoria, città e paese (2 lettere)."); return; }
    setSaving(true);
    try {
      await api.post(`/professional/activate`, form);
      setShowDisclaimer(false);
      await load();
      alert("ADE Pro attivato");
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore attivazione");
    } finally { setSaving(false); }
  };

  const doUpdate = async () => {
    setSaving(true);
    try {
      await api.put(`/professional/profile`, form);
      await load();
      alert("Profilo aggiornato");
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const doDeactivate = async () => {
    if (!window.confirm("Tornare a Profilo Personale? L'audience resterà in pausa e il profilo scomparirà dalla ricerca globale.")) return;
    setSaving(true);
    try {
      await api.post(`/professional/deactivate`, {});
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const toggleSearchable = async (val) => {
    const prev = state.searchable;
    setState((s) => ({ ...s, searchable: val }));
    try {
      await api.patch(`/professional/settings`, { searchable: val });
    } catch (e) {
      // Rollback on API failure
      setState((s) => ({ ...s, searchable: prev }));
      alert(e?.response?.data?.detail || "Errore aggiornamento visibilità");
    }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const renderField = (k, label, opts = {}) => {
    const { type = "text", placeholder = "", area = false } = opts;
    return (
      <label className="block" key={k}>
        <span className="text-xs font-medium text-gray-500">{label}</span>
        {area ? (
          <textarea
            value={form[k]}
            onChange={(e) => setField(k, e.target.value)}
            placeholder={placeholder}
            rows={4}
            className="mt-1 w-full text-sm px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 outline-none"
            data-testid={`prosettings-${k}`}
          />
        ) : (
          <input
            type={type}
            value={form[k]}
            onChange={(e) => setField(k, e.target.value)}
            placeholder={placeholder}
            className="mt-1 w-full text-sm px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 outline-none"
            data-testid={`prosettings-${k}`}
          />
        )}
      </label>
    );
  };

  if (state.loading) return <div className="min-h-screen flex items-center justify-center text-gray-500">…</div>;

  return (
    <div className="min-h-screen set-glass-page" data-testid="pro-settings-page">
      <Seo title="ADE Pro · ADE" description="Attiva o gestisci il tuo profilo ADE Pro" />
      <div className="sticky top-0 z-10 set-glass-header flex items-center gap-3 px-4 py-3">
        <button onClick={() => navigate(-1)} className="p-1" data-testid="prosettings-back">
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <h1 className="text-sm font-semibold text-gray-800 flex-1">ADE Pro</h1>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200">
          <Building2 className="w-3 h-3" /> PRO
        </span>
      </div>

      <div className="px-5 py-5 space-y-4 max-w-xl mx-auto">
        {!isPro && (
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
            <h2 className="font-semibold text-gray-900">Passa ad ADE Pro</h2>
            <p className="text-sm text-gray-600 mt-1">Rendi la tua attività scopribile su ADE attraverso la ricerca globale, senza cambiare il tuo account personale.</p>
          </div>
        )}

        <div className="set-glass-row p-4 grid grid-cols-1 gap-3">
          {renderField("professional_name", "Nome dell'attività", { placeholder: "Es. Studio Bianchi" })}
          <label className="block">
            <span className="text-xs font-medium text-gray-500">Tipo</span>
            <select
              value={form.professional_type}
              onChange={(e) => setForm({ ...form, professional_type: e.target.value })}
              className="mt-1 w-full text-sm px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 outline-none"
              data-testid="prosettings-professional_type"
            >
              {PROFESSIONAL_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            {(() => {
              const selectedCat = categories.find((c) => c.key === form.category);
              const legacyCat = form.category && !categories.some((c) => c.key === form.category);
              const subcats = selectedCat?.subcategories || [];
              const legacySub = form.subcategory && !subcats.includes(form.subcategory);
              return (
                <>
                  <label className="block">
                    <span className="text-xs font-medium text-gray-500">Categoria</span>
                    <select
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value, subcategory: "" })}
                      className="mt-1 w-full text-sm px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 outline-none"
                      data-testid="prosettings-category"
                    >
                      <option value="">Seleziona categoria…</option>
                      {legacyCat && <option value={form.category}>{form.category}</option>}
                      {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-gray-500">Sottocategoria (opzionale)</span>
                    <select
                      value={form.subcategory}
                      onChange={(e) => setForm({ ...form, subcategory: e.target.value })}
                      disabled={!form.category || (subcats.length === 0 && !legacySub)}
                      className="mt-1 w-full text-sm px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 outline-none disabled:opacity-50"
                      data-testid="prosettings-subcategory"
                    >
                      <option value="">—</option>
                      {legacySub && <option value={form.subcategory}>{form.subcategory}</option>}
                      {subcats.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                </>
              );
            })()}
          </div>
          {renderField("extended_bio", "Descrizione", { area: true, placeholder: "Racconta la tua attività (max 2000 char)" })}
          {renderField("website_url", "Sito web", { placeholder: "https://…" })}
          <div className="grid grid-cols-2 gap-2">
            {renderField("business_email", "Email business", { placeholder: "opzionale" })}
            {renderField("business_phone", "Telefono business", { placeholder: "opzionale" })}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {renderField("city", "Città", { placeholder: "Es. Bolzano" })}
            {renderField("country", "Paese (ISO 2)", { placeholder: "IT" })}
          </div>
          {renderField("address", "Indirizzo", { placeholder: "opzionale" })}
          <label className="flex items-center gap-2 py-1">
            <input
              type="checkbox"
              checked={form.accepts_inbound_dm}
              onChange={(e) => setForm({ ...form, accepts_inbound_dm: e.target.checked })}
              data-testid="prosettings-accepts_inbound_dm"
            />
            <span className="text-sm text-gray-700">Consenti ai fan di scriverti in chat</span>
          </label>
        </div>

        {/* Actions */}
        <div className="pt-3 space-y-2">
          {!isPro ? (
            <button
              onClick={() => setShowDisclaimer(true)}
              disabled={!canSubmit || saving}
              className="w-full py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-40"
              data-testid="prosettings-activate"
            >
              Attiva ADE Pro
            </button>
          ) : (
            <>
              <button
                onClick={doUpdate}
                disabled={!canSubmit || saving}
                className="w-full py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-40"
                data-testid="prosettings-update"
              >
                Salva modifiche
              </button>
              <button
                onClick={() => navigate('/app/professional/analytics')}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-semibold active:scale-[0.99] transition-transform"
                data-testid="prosettings-analytics"
              >
                Dashboard ADE Pro ›
              </button>
              <button
                onClick={() => navigate('/app/pro/missions')}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-fuchsia-600 to-violet-600 text-white text-sm font-semibold active:scale-[0.99] transition-transform"
                data-testid="prosettings-missions"
              >
                ADE Missions · Campagne pubblicitarie ›
              </button>
              <button
                onClick={() => navigate('/app/professional/audience')}
                className="w-full py-3 rounded-xl border border-blue-200 bg-blue-50 text-blue-700 text-sm font-semibold hover:bg-blue-100"
                data-testid="prosettings-audience"
              >
                Gestisci Audience ›
              </button>
              <label className="flex items-center justify-between px-4 py-3 set-glass-row">
                <span className="text-sm text-gray-700">Visibile nella ricerca globale</span>
                <input
                  type="checkbox"
                  checked={state.searchable}
                  onChange={(e) => toggleSearchable(e.target.checked)}
                  data-testid="prosettings-searchable-toggle"
                />
              </label>
              <button
                onClick={doDeactivate}
                disabled={saving}
                className="w-full py-3 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200"
                data-testid="prosettings-deactivate"
              >
                Torna al profilo personale
              </button>
            </>
          )}
        </div>
      </div>

      {/* Disclaimer modal */}
      {showDisclaimer && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-end sm:items-center justify-center p-4" data-testid="prosettings-disclaimer">
          <div className="bg-white rounded-2xl max-w-md w-full p-5">
            <h3 className="font-semibold text-gray-900 text-lg">Conferma attivazione</h3>
            <p className="mt-3 text-sm text-gray-700">
              Il tuo profilo <strong>ADE Pro</strong> potrà essere trovato tramite la ricerca globale di ADE.
              I tuoi contenuti personali (post e Stories privati, chat, chiamate) restano invariati e riservati ai tuoi Collegamenti personali.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-2">
              <button onClick={() => setShowDisclaimer(false)} className="py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold" data-testid="prosettings-cancel">
                Annulla
              </button>
              <button onClick={doActivate} disabled={saving} className="py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-50" data-testid="prosettings-confirm">
                Attiva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
